import React, { useEffect } from 'react';
import { getOptimizedImageUrl } from '../lib/cms';
import { getImageAlt } from '../lib/images';

export default function Lightbox({ isOpen, images = [], currentIndex = 0, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, onPrev, onNext]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-espresso/90 backdrop-blur-md p-4 sm:p-8 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-11 h-11 text-white hover:text-sand flex items-center justify-center transition-colors cursor-pointer focus:outline-hidden"
        aria-label="Luk visning"
      >
        <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
      </button>

      {/* Counter */}
      <div className="absolute top-5 left-6 z-10 text-xs font-medium tracking-widest text-white/80 uppercase">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 sm:left-6 z-10 w-12 h-12 text-white hover:text-sand flex items-center justify-center transition-colors cursor-pointer focus:outline-hidden"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-2xl" aria-hidden="true" />
        </button>
      )}

      {/* Image container */}
      <div 
        className="max-w-5xl max-h-[85vh] flex items-center justify-center select-none"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getOptimizedImageUrl(currentImage, { width: 1600, quality: 90 })}
          alt={getImageAlt(currentImage, `Galleri billede ${currentIndex + 1}`)}
          className="max-w-full max-h-[82vh] object-contain shadow-2xl transition-all duration-200"
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 sm:right-6 z-10 w-12 h-12 text-white hover:text-sand flex items-center justify-center transition-colors cursor-pointer focus:outline-hidden"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-2xl" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
